import { Popover } from '@base-ui/react';
import { ValidationErrorMessage } from '@components/formInputs/ValidationErrorMessage';
import { Icon } from '@components/icon/Icon';
import { Modal } from '@components/modal/Modal';
import { errorToast } from '@components/toasts/Toasts';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import { useCallback, useRef, useState, type RefObject } from 'react';
import type { Control, FieldPath, UseFormSetValue } from 'react-hook-form';
import { useController, useWatch } from 'react-hook-form';
import { getEventImageUploadUrl } from '../../apiHelpers';
import {
  isBlankAddOnRow,
  type EventAddOnFormItem,
  type EventFormData,
} from '../RestaurantEventForm';
import {
  getIconComponent,
  PeakLibraryIconPicker,
  type PeakLibraryIconName,
  renderIconToSvgString,
} from './PeakLibraryIconPicker';
import styles from './AddonImageField.scss';

export type AddonImageFieldProps = {
  control: Control<EventFormData>;
  /** Row index in `addOns` — used to skip photo validation when the row is empty. */
  index: number;
  imageKeyField: FieldPath<EventFormData>;
  restaurantId: string;
  setValue: UseFormSetValue<EventFormData>;
  /** Used with tier popover and photo menu to resolve the modal portal root. */
  layoutRef: RefObject<HTMLElement | null>;
};

export const AddonImageField = ({
  control,
  index,
  imageKeyField,
  restaurantId,
  setValue,
  layoutRef,
}: AddonImageFieldProps) => {
  const addOnRowPath = `addOns.${index}` as FieldPath<EventFormData>;
  const addOnRow = useWatch({ control, name: addOnRowPath }) as
    | EventAddOnFormItem
    | undefined;

  const formImageKey = String(addOnRow?.imageKey ?? '').trim();

  const {
    fieldState: { error: imageKeyError },
  } = useController({
    control,
    name: imageKeyField,
    rules: {
      validate: (value) => {
        if (isBlankAddOnRow(addOnRow)) return true;
        if (!String(value ?? '').trim()) return 'Photo is required';
        return true;
      },
    },
  });

  const [peakLibrarySelectedIcon, setPeakLibrarySelectedIcon] =
    useState<PeakLibraryIconName | null>(null);
  const [customImagePreviewUrl, setCustomImagePreviewUrl] = useState<
    string | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoMenuPortalContainer, setPhotoMenuPortalContainer] =
    useState<HTMLElement | null>(null);

  const [isPhotoMenuOpen, setIsPhotoMenuOpen] = useState(false);
  const {
    isOpen: isPeakLibraryIconSelectionModalOpen,
    open,
    close,
  } = useIsOpen();

  const handlePhotoMenuOpenChange = useCallback(
    (openMenu: boolean) => {
      if (openMenu) {
        const dialog = layoutRef.current?.closest('[role="dialog"]');
        setPhotoMenuPortalContainer(
          dialog instanceof HTMLElement ? dialog : null,
        );
      }
      setIsPhotoMenuOpen(openMenu);
    },
    [layoutRef],
  );

  const uploadSvgToS3 = async (
    iconName: PeakLibraryIconName,
  ): Promise<string> => {
    const svgString = renderIconToSvgString(iconName);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });

    const { signedUploadImageUrl, objectKey } =
      await getEventImageUploadUrl(restaurantId);

    await fetch(signedUploadImageUrl, {
      method: 'PUT',
      body: svgBlob,
      headers: {
        'Content-Type': 'image/svg+xml',
      },
    });

    return objectKey;
  };

  const handlePeakLibraryIconSelection = async (
    iconName: PeakLibraryIconName,
  ) => {
    setPeakLibrarySelectedIcon(iconName);
    setCustomImagePreviewUrl(null);
    try {
      const objectKey = await uploadSvgToS3(iconName);
      setValue(imageKeyField, objectKey, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setPeakLibrarySelectedIcon(null);
      close();
    } catch (_error) {
      errorToast({ message: 'Failed to save icon. Please try again' });
    }
  };

  const SelectedPeakLibraryIconComponent = peakLibrarySelectedIcon
    ? getIconComponent(peakLibrarySelectedIcon)
    : null;

  const displayImageUrl = (() => {
    if (customImagePreviewUrl) return customImagePreviewUrl;
    if (formImageKey) return getRestaurantImagePath(formImageKey);
    return null;
  })();

  const handleUploadFromComputer = () => {
    setIsPhotoMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleChooseFromLibrary = () => {
    setIsPhotoMenuOpen(false);
    open();
  };

  const handleFileChange = async (
    changeEvent: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = changeEvent.target.files?.[0];
    if (!file) return;

    try {
      const { signedUploadImageUrl, objectKey } =
        await getEventImageUploadUrl(restaurantId);

      await fetch(signedUploadImageUrl, {
        method: 'PUT',
        body: file,
      });

      setCustomImagePreviewUrl(URL.createObjectURL(file));
      setPeakLibrarySelectedIcon(null);
      setValue(imageKeyField, objectKey, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } catch (_error) {
      errorToast({ message: 'Failed to upload image. Please try again' });
    } finally {
      changeEvent.target.value = '';
    }
  };

  return (
    <>
      <div className={styles.addonPhotoColumn}>
        <Popover.Root
          open={isPhotoMenuOpen}
          onOpenChange={handlePhotoMenuOpenChange}
        >
          {SelectedPeakLibraryIconComponent && (
            <Popover.Trigger
              className={styles.selectedIconButton}
              type="button"
            >
              <SelectedPeakLibraryIconComponent
                className={styles.selectedIcon}
              />
            </Popover.Trigger>
          )}

          {displayImageUrl && (
            <div className={styles.existingImageContainer}>
              <img src={displayImageUrl} alt="Add-on" />
              <Popover.Trigger
                className={styles.changePhotoButton}
                type="button"
              >
                Change Photo
              </Popover.Trigger>
            </div>
          )}

          {!SelectedPeakLibraryIconComponent && !displayImageUrl && (
            <Popover.Trigger className={styles.addPhoto} type="button">
              + Add Photo
            </Popover.Trigger>
          )}

          <Popover.Portal container={photoMenuPortalContainer ?? undefined}>
            <Popover.Positioner
              collisionBoundary={
                photoMenuPortalContainer ?? document.documentElement
              }
              positionMethod="fixed"
              side="bottom"
              sideOffset={8}
              className={styles.photoMenuPositioner}
            >
              <Popover.Popup className={styles.photoMenu}>
                <button
                  className={styles.photoMenuItem}
                  onClick={handleUploadFromComputer}
                  type="button"
                >
                  <Icon name="upload" className={styles.photoMenuItemIcon} />
                  Upload from Computer
                </button>
                <button
                  className={styles.photoMenuItem}
                  onClick={handleChooseFromLibrary}
                  type="button"
                >
                  <Icon
                    name="mountainRange"
                    className={styles.photoMenuItemIcon}
                  />
                  Choose from Peak Library
                </button>
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          data-testid="addon-photo-upload"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <ValidationErrorMessage<EventFormData>
          error={imageKeyError}
          label="Photo"
          name={imageKeyField}
        />
      </div>

      <Modal
        isOpen={isPeakLibraryIconSelectionModalOpen}
        onClose={close}
        title="Choose from Peak Library"
      >
        <PeakLibraryIconPicker
          selectedIcon={peakLibrarySelectedIcon}
          onSelect={handlePeakLibraryIconSelection}
        />
      </Modal>
    </>
  );
};
